<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <h1>
          {{ $tc('models.shipment', 2) }}
          <v-btn color="primary" @click="createShipment"> {{ $t('create') }} </v-btn>
          <v-btn color="primary" @click="sync" :loading="syncing" :disabled="syncing" v-if="hasStores"> {{ $t('syncStores') }} </v-btn>
          <v-btn color="primary" @click="goTo('importShipmentsCsv')">{{ $t('csvShipmentButton') }}</v-btn>
          <v-btn color="primary" v-if="$auth.isDev() || $auth.currentUser.export_csv_visible" @click="exportShipments" :loading="csvLoading" :disabled="csvLoading">{{ $t('shipmentsExport') }}</v-btn>
        </h1>
      </v-flex>

      <ShipmentFilter :enums="enums" @fetch="fetch"></ShipmentFilter>

      <v-flex xs12>
        <v-data-table class="elevation-1"
                      :headers="headers"
                      hide-headers
                      :items="items"
                      :loading="loading"
                      :total-items="totalItems"
                      :pagination.sync="pagination"
                      item-key="id"
                      :no-data-text="$t('noDataAvailable')">
          <template v-slot:items="props">
            <tr>
              <td width="5%" @click="props.expanded = !props.expanded">
                {{ props.item.sequential_id || props.item.id }}
              </td>

              <td @click="props.expanded = !props.expanded">
                {{ props.item.destination.name }}
              </td>

              <td @click="props.expanded = !props.expanded">
                {{ props.item.destination.street }}
              </td>

              <td @click="props.expanded = !props.expanded">
                <Status :shipment="props.item"></Status>
              </td>

              <td class="text-xs-right">
                <Pack :shipment="props.item"></Pack>
                <SelectRate :shipment="props.item"></SelectRate>
                <Download :shipment="props.item"></Download>
                <DownloadNotaLabelBtn :shipment="props.item" :label-format="labelFormat" :terms-conditions-link="termsAndConditions" :app-logo="appLogo"></DownloadNotaLabelBtn>
                <GenerateLabel :shipment="props.item"></GenerateLabel>
              </td>

              <td class="pa-0 pr-2 text-xs-right">
                <v-btn color="primary" flat icon v-if="!props.expanded" @click="props.expanded = !props.expanded">
                  <font-awesome-icon icon="angle-left"></font-awesome-icon>
                </v-btn>

                <v-btn color="primary" flat icon v-if="props.expanded" @click="props.expanded = !props.expanded">
                  <font-awesome-icon icon="angle-down"></font-awesome-icon>
                </v-btn>
              </td>
            </tr>
          </template>

          <template v-slot:expand="props">
            <ShipmentDetails :shipment="props.item" @fetch="fetch"></ShipmentDetails>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Enum from '@/client/enum'
import Store from '@/client/store'
import Shipment from '@/client/shipment'
import Setting from '@/client/setting'

import ShipmentFilter from '@/components/shipments/filter'
import Status from '@/components/shipments/btns/status'
import Pack from '@/components/shipments/btns/pack'
import SelectRate from '@/components/shipments/btns/select-rate'
import Download from '@/components/shipments/btns/download'
import ShipmentDetails from '@/components/shipments/details'
import GenerateLabel from '@/components/shipments/btns/add-label'
import DownloadNotaLabelBtn from '@/components/shipments/btns/download-nota-label'

export default {
  name: 'ShipmentsView',

  props: {},

  data () {
    return {
      headers: [
        { text: '', align: 'left', sortable: false, value: '' },
        { text: '', align: 'left', sortable: false, value: '' },
        { text: '', align: 'left', sortable: false, value: '' },
        { text: '', align: 'left', sortable: false, value: '' },
        { text: '', align: 'left', sortable: false, value: '' },
        { text: '', align: 'left', sortable: false, value: '' }
      ],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },
      items: [],
      loading: false,
      syncing: false,
      enums: {},
      stores: [],
      mountedFirst: true,
      params: {},
      csvLoading: false,
      labelFormat: 'letter',
      termsAndConditions: '',
      appLogo: ''
    }
  },

  components: {
    ShipmentFilter,
    Status,
    Pack,
    SelectRate,
    Download,
    ShipmentDetails,
    GenerateLabel,
    DownloadNotaLabelBtn
  },

  computed: {
    hasStores () {
      if (!this.stores) { return false }

      return this.stores.length > 0
    }
  },

  methods: {
    async setup () {
      this.enums = await Enum.all()
      this.stores = await Store.all()
    },

    async fetch (params = {}, filter = false) {
      this.loading = true
      if (filter) this.params = params

      const { sortBy, descending, page, rowsPerPage } = this.pagination
      let order = ''
      if (sortBy) {
        order = `shipments.${sortBy} ${descending ? 'desc' : 'asc'}`
      }

      let where = {}
      if (this.params.status) { where.status = this.params.status }
      if (this.params.source) { where.source = this.params.source }
      if (this.params.orderId) { where.order_id = this.params.orderId }
      if ( (this.params.source == 'shopify' || this.params.source == 'woocommerce') && this.params.store_id ) { where.store_id = this.params.store_id }

      if (this.params.trackingNumber) { where.labels = {labels: { tracking_number: this.params.trackingNumber }} }

      if (this.params.shipmentId) {
        let str = '(id = ? OR source_number = ?)'
        // TODO remove this after migrations
        const tenantId = process.env.VUE_APP_TENANT_ID
        if (tenantId !== '') {
          str = `(sequential_id = ? OR source_number = ?)`
        }
        if (this.params.source) { str += ` AND source = ?` }

        where = [
          str,
          this.params.shipmentId,
          this.params.shipmentId
        ]
        if (this.params.source) { where.push(this.enums.shipments_source[this.params.source]) }
      } else {
        if (this.params.email) {
          where.users = {}
          where.users.email = this.params.email
        }

        if (this.params.carrierId) {
          where.rates = { services: { carrier_id: this.params.carrierId } }
        }
      }

      this.items = await Shipment.all({
        order,
        page,
        per_page: rowsPerPage,
        where
      })

      this.totalItems = this.items.pagination.total_objects
      this.loading = false

    },

    async sync () {
      if (this.syncing) { return }

      this.syncing = true
      try {
        await Shipment.sync()
        this.fetch()
      } catch (err) {
        this.handleErr(err)
      }
      this.syncing = false
    },

    createShipment () {
      window.sessionStorage.removeItem('new_shipment')
      this.goTo('newShipment')
    },

    async exportShipments () {
      this.csvLoading = true
      try {
        const data = await Shipment.exportCsv()
        this.downloadFile(data, 'shipments.csv')
      } catch (err) {
        this.handleBlobErr(err)
      }
      this.csvLoading = false
    },

    downloadFile (data, filename) {
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    handleBlobErr (err) {
      const self = this
      if (err.response) {
        const blob = err.response.data
        const reader = new FileReader()

        // This fires after the blob has been read/loaded.
        reader.addEventListener('loadend', (e) => {
          const text = e.srcElement.result
          self.handleErr({ response: { data: JSON.parse(text) } })
        })

        // Start reading the blob as text.
        reader.readAsText(blob)
      } else {
        this.handleErr(err)
      }
    },

    async getAppLogo () {
      try {
        const data = await Shipment.getAppLogo()
        return data.text()
      } catch (err) {
        this.handleBlobErr(err)
      }
    }
  },

  watch: {
    pagination: {
      handler () {
        if (this.mountedFirst && this.$route.query.shipment_id) {
          this.fetch({ shipmentId: this.$route.query.shipment_id }, true)
          this.mountedFirst = false
        }
        else {
          this.fetch()
        }
      },
    }
  },

  async mounted () {
    try {
      this.appLogo = await this.getAppLogo()
    } catch (err) {
      // null
    }

    this.settings = await Setting.all()

    const labelFormatSetting = this.settings.find(o => o.key === 'nota_label_format') || {}
    if (labelFormatSetting.value) this.labelFormat = labelFormatSetting.value

    this.setup()
  }
}
</script>
